import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AIClipTaskDetail,
  getAIClipTask,
  getAIClipTaskRecord,
  getAIClipTaskWarnings,
} from '../../../api/aiClip';
import { isApiError } from '../../../api/errors';
import AIClipRecordView from './AIClipRecordView';
import AIClipWarningsView, { countDroppedWarnings, countWarnings } from './AIClipWarningsView';

interface AIClipResultViewProps {
  episodeId: string;
  taskId: string;
  onPremiereDownloadUrlChange?: (url?: string) => void;
}

type RecordLoadState =
  | { phase: 'loading' }
  | { phase: 'loaded'; value: unknown }
  | { phase: 'failed' };

type WarningsLoadState =
  | { phase: 'loading' }
  | { phase: 'loaded'; value: unknown }
  | { phase: 'missing' }
  | { phase: 'failed' };

type ResultTab = 'record' | 'warnings';

type DetailLoadState =
  | { phase: 'loading' }
  | { phase: 'loaded'; value: AIClipTaskDetail }
  | { phase: 'failed' };

const MALFORMED_BADGE = '!';

function badgeFor(warnings: unknown): number | string | undefined {
  const total = countWarnings(warnings);
  if (total === null) return undefined;
  if (total > 0) return total;
  return countDroppedWarnings(warnings) > 0 ? MALFORMED_BADGE : undefined;
}

const AIClipResultView = ({
  episodeId, taskId, onPremiereDownloadUrlChange,
}: AIClipResultViewProps) => {
  const { t } = useTranslation('episodeDetail');
  const [detailState, setDetailState] = useState<DetailLoadState>({ phase: 'loading' });
  const [recordState, setRecordState] = useState<RecordLoadState>({ phase: 'loading' });
  const [warningsState, setWarningsState] = useState<WarningsLoadState>({ phase: 'loading' });
  const [activeTab, setActiveTab] = useState<ResultTab>('record');
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setDetailState({ phase: 'loading' });
    setRecordState({ phase: 'loading' });
    setWarningsState({ phase: 'loading' });
    setActiveTab('record');
    onPremiereDownloadUrlChange?.(undefined);

    getAIClipTask(episodeId, taskId)
      .then(value => {
        if (!cancelled) {
          setDetailState({ phase: 'loaded', value });
          onPremiereDownloadUrlChange?.(
            value.status === 'succeeded' ? value.premiereDownloadUrl : undefined,
          );
        }
      })
      .catch(err => {
        if (cancelled) return;
        setDetailState({ phase: 'failed' });
        // eslint-disable-next-line no-console
        console.warn(`[AIClipResultView] detail load failed episodeId=${episodeId} taskId=${taskId}`, err);
      });
    getAIClipTaskRecord(episodeId, taskId)
      .then(value => {
        if (!cancelled) setRecordState({ phase: 'loaded', value });
      })
      .catch(err => {
        if (cancelled) return;
        setRecordState({ phase: 'failed' });
        // eslint-disable-next-line no-console
        console.warn(`[AIClipResultView] record load failed episodeId=${episodeId} taskId=${taskId}`, err);
      });
    getAIClipTaskWarnings(episodeId, taskId)
      .then(value => {
        if (!cancelled) setWarningsState({ phase: 'loaded', value });
      })
      .catch(err => {
        if (cancelled) return;
        if (isApiError(err) && err.status === 404) {
          setWarningsState({ phase: 'missing' });
          return;
        }
        setWarningsState({ phase: 'failed' });
        // eslint-disable-next-line no-console
        console.warn(`[AIClipResultView] warnings load failed episodeId=${episodeId} taskId=${taskId}`, err);
      });

    return () => { cancelled = true; };
  }, [episodeId, onPremiereDownloadUrlChange, retryNonce, taskId]);

  const warningsBadge = warningsState.phase === 'loaded' ? badgeFor(warningsState.value) : undefined;
  const detail = detailState.phase === 'loaded' ? detailState.value : null;

  if (detailState.phase === 'loading') {
    return <p>{t('aiClip.resultLoading')}</p>;
  }

  if (detailState.phase === 'failed') {
    return (
      <div role="alert">
        <p style={{ color: 'var(--danger, #d33)' }}>{t('aiClip.resultLoadFailed')}</p>
        <button type="button" className="btn btn-secondary" onClick={() => setRetryNonce(value => value + 1)}>
          {t('aiClip.workspace.retry')}
        </button>
      </div>
    );
  }

  if (!detail || detail.status !== 'succeeded') {
    return <p>{t('aiClip.noViewableResult')}</p>;
  }

  return (
    <div>
      <style>{`
        @media (max-width: 720px) {
          .ai-clip-result-layout { flex-direction: column; height: auto !important; }
          .ai-clip-result-video { height: 34vh; flex: none !important; }
          .ai-clip-result-tabs { min-height: 34vh; flex: none !important; }
        }
      `}</style>
      <div className="ai-clip-result-layout" style={{ display: 'flex', gap: '1rem', alignItems: 'stretch', height: '70vh' }}>
        <div className="ai-clip-result-video" style={{ flex: 3, minWidth: 0 }}>
          {detail?.videoPlayUrl && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              controls
              src={detail.videoPlayUrl}
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', borderRadius: '8px' }}
            />
          )}
        </div>
        <div className="ai-clip-result-tabs" style={{ flex: 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid var(--border)' }}>
            <TabButton
              active={activeTab === 'record'}
              onClick={() => setActiveTab('record')}
              label={t('aiClip.recordTitle')}
            />
            <TabButton
              active={activeTab === 'warnings'}
              onClick={() => setActiveTab('warnings')}
              label={t('aiClip.warnings.tab')}
              badge={warningsBadge}
            />
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {activeTab === 'record' && (
              <>
                {recordState.phase === 'failed' && (
                  <p style={{ fontSize: '0.85rem', color: 'var(--danger, #d33)' }}>
                    {t('aiClip.recordLoadFailed')}
                  </p>
                )}
                {recordState.phase === 'loaded' && <AIClipRecordView record={recordState.value} />}
              </>
            )}
            {activeTab === 'warnings' && (
              <>
                {warningsState.phase === 'loading' && (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-3)' }}>
                    {t('aiClip.warnings.loading')}
                  </p>
                )}
                {warningsState.phase === 'missing' && (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-3)' }}>
                    {t('aiClip.warnings.noRecord')}
                  </p>
                )}
                {warningsState.phase === 'failed' && (
                  <p style={{ fontSize: '0.85rem', color: 'var(--danger, #d33)' }}>
                    {t('aiClip.warnings.loadFailed')}
                  </p>
                )}
                {warningsState.phase === 'loaded' && <AIClipWarningsView warnings={warningsState.value} />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  badge?: number | string;
}

function TabButton({ active, onClick, label, badge }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.6rem',
        border: 'none', borderBottom: active ? '2px solid var(--primary, #0075de)' : '2px solid transparent',
        background: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: active ? 600 : 400,
        color: active ? 'var(--text-1, inherit)' : 'var(--text-3, #999)',
      }}
    >
      {label}
      {badge !== undefined && (
        <span style={{
          background: 'rgba(180, 83, 9, 0.15)', color: 'var(--warning, #b45309)',
          borderRadius: '999px', padding: '0 6px', fontSize: '0.7rem', fontWeight: 600,
        }}>
          {badge}
        </span>
      )}
    </button>
  );
}

export default AIClipResultView;
